import { type FC, type ReactNode, useEffect, useRef, useState } from "react";
import { type Placement } from "@popperjs/core";
import { usePopper } from "react-popper";
import { createPortal } from "react-dom";
import styles from "./Dropdown.module.css";
import Tooltip from "./Tooltip";

export type Option = {
  value: string;
  label: string;
  disabled?: boolean;
  divider?: boolean;
  onClick?: (option: Option) => unknown;
  render?: (option: Option) => JSX.Element;
  className?: string;
};

interface Props {
  isOpen: boolean;
  trigger: JSX.Element;
  onClickOutside?: () => void;
  onSelect?: () => void;
  header?: React.ReactNode;
  body?: JSX.Element;
  footer?: JSX.Element;
  placement?: Placement;
  options?: Option[];
  className?: string;
  optionsClassName?: string;
  optionsContainerClassName?: string;
  dropdownClassName?: string;
  role?: string;
  ref?: React.RefObject<HTMLDivElement>;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
}

const WrapOptionWithTooltip: FC<{ tooltip: string; children?: ReactNode }> = ({
  children,
  tooltip,
}) => {
  return (
    <Tooltip
      delay={2000}
      disabled={!tooltip}
      placement={"bottom"}
      trigger={children}
    >
      {tooltip}
    </Tooltip>
  );
};

const Dropdown: FC<Props> = ({
  isOpen,
  trigger,
  options = [],
  className = "",
  optionsClassName = "",
  optionsContainerClassName = "",
  dropdownClassName = "",
  placement = "bottom-start",
  role,
  header,
  body,
  footer,
  onClickOutside,
  onSelect,
  onKeyDown,
}) => {
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );
  const portalRef = useRef<Element | null>(null);
  const [mounted, setMounted] = useState(false);

  const scrollPosition = useRef(0);
  const optionsRef = useRef<HTMLDivElement>(null);

  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  const focusableElsRef = useRef<HTMLElement[]>([]);

  useEffect(() => {
    portalRef.current = document.getElementById("dropdown-portal");
    setMounted(true);
  }, []);

  const { styles: pooperStyles, attributes } = usePopper(
    referenceElement,
    popperElement,
    {
      placement,
    }
  );

  useEffect(() => {
    const optionsContainer = optionsRef.current;
    if (isOpen && optionsContainer) {
      optionsContainer.scrollTop = scrollPosition.current;
      previouslyFocusedElementRef.current =
        document.activeElement as HTMLElement;
      if (popperElement) {
        const focusableSelectors =
          'a[href], button, textarea, input:not([tabindex="-1"]), select, [tabindex]:not([tabindex="-1"])';
        focusableElsRef.current = Array.from(
          popperElement.querySelectorAll<HTMLElement>(focusableSelectors)
        ).filter((el) => !el.hasAttribute("disabled"));
      }
      if (focusableElsRef.current.length > 0) {
        focusableElsRef.current[0]?.focus();
      }
    } else {
      if (previouslyFocusedElementRef.current) {
        previouslyFocusedElementRef.current.focus();
      }
    }
  }, [isOpen, popperElement]);

  useEffect(() => {
    const optionsContainer = optionsRef.current;
    if (isOpen && optionsContainer) {
      optionsContainer.scrollTop = scrollPosition.current;
    }

    const handleScroll = () => {
      if (optionsContainer) {
        scrollPosition.current = optionsContainer.scrollTop;
      }
    };

    if (optionsContainer) {
      optionsContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (optionsContainer) {
        optionsContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isOpen, optionsRef]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;
      if (target.closest('[data-dropdown-root="true"]')) return;
      if (
        popperElement &&
        !popperElement.contains(event.target as Node) &&
        referenceElement &&
        !referenceElement.contains(event.target as Node)
      ) {
        document.body.style.pointerEvents = "none";
        onClickOutside?.();
        setTimeout(() => {
          document.body.style.pointerEvents = "auto";
        }, 100);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, popperElement, referenceElement, onClickOutside]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(e);

    if (!isOpen) return;

    const focusableEls = focusableElsRef.current;
    if (!focusableEls.length) return;

    if (e.key === "Tab") {
      e.preventDefault();

      const currentIndex = focusableEls.findIndex(
        (el) => el === document.activeElement
      );
      const lastIndex = focusableEls.length - 1;

      let nextIndex;
      if (e.shiftKey) {
        nextIndex = currentIndex <= 0 ? lastIndex : currentIndex - 1;
      } else {
        nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
      }
      focusableEls[nextIndex]?.focus();
    }

    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();

      const currentIndex = focusableEls.findIndex(
        (el) => el === document.activeElement
      );
      const lastIndex = focusableEls.length - 1;

      let nextIndex;
      if (e.key === "ArrowDown") {
        nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
      } else {
        nextIndex = currentIndex <= 0 ? lastIndex : currentIndex - 1;
      }

      focusableEls[nextIndex]?.focus();
    }
  };

  const handleClick = (selectedValue: Option) => {
    if (selectedValue.disabled) return;
    selectedValue.onClick?.(selectedValue);
    if (onSelect) onSelect();
  };

  return (
    <div role={"menu"} className={`${styles.container} ${className}`}>
      <div role={role} className={"relative"} ref={setReferenceElement}>
        {trigger}
      </div>
      {mounted && portalRef.current
        ? createPortal(
            <>
              {isOpen && (
                <>
                  <div
                    ref={setPopperElement}
                    className={`${dropdownClassName} ${styles.dropdown}`}
                    style={{
                      ...pooperStyles.popper,
                      width: referenceElement?.clientWidth,
                    }}
                    data-dropdown-root="true"
                    {...attributes.popper}
                    onKeyDown={handleKeyDown}
                  >
                    {header}
                    {body ?? (
                      <div
                        ref={optionsRef}
                        className={`${styles.options} ${optionsContainerClassName}`}
                      >
                        {options.map((option, index) => (
                          <WrapOptionWithTooltip
                            key={option.value}
                            tooltip={option.label}
                          >
                            <div
                              role={"menuitem"}
                              tabIndex={option.disabled ? -1 : 0}
                              id={`menuitem-${role}-${index}`}
                              className={`flex w-full ${
                                styles.option
                              } ${optionsClassName} ${option.className} ${
                                option.disabled ? styles.optionDisabled : ""
                              } ${option.divider ? styles.optionDivider : ""}`}
                              onClick={(e) => (
                                e.stopPropagation(),
                                handleClick(option)
                              )}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  option.onClick?.(option);
                                }
                              }}
                            >
                              {option.render ? (
                                option.render(option)
                              ) : (
                                <div>{option.label}</div>
                              )}
                            </div>
                          </WrapOptionWithTooltip>
                        ))}
                      </div>
                    )}
                    {footer}
                  </div>
                </>
              )}
            </>,
            portalRef.current
          )
        : null}
    </div>
  );
};

export const useDropdownControls = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen((prev) => !prev);

  return {
    isOpen,
    setIsOpen,
    toggle,
  };
};

export default Dropdown;
